import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowRight, Calendar, MapPin } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import { partners, getPartnerSlug } from '../data/partners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SectionHeading = ({ children, count }) => (
  <div className="flex items-baseline gap-3 mb-8">
    <h2 className="text-2xl md:text-3xl text-[#2C2C2C]" style={{ fontFamily: '"Playfair Display", serif' }}>
      {children}
    </h2>
    <span className="text-sm text-[#DE92A9] font-medium">{count}</span>
  </div>
);

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setInput(q);
    if (q.trim().length < 2) {
      setResults(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${BACKEND_URL}/api/search`, { params: { q } });
        if (!cancelled) setResults(response.data);
      } catch (error) {
        if (!cancelled) setResults({ query: q, total: 0, articles: [], weddings: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [q]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (trimmed.length >= 2) setSearchParams({ q: trimmed });
  };

  const term = q.trim().toLowerCase();
  const matchedPartners = term.length >= 2
    ? partners.filter((p) =>
        [p.name, p.category, p.description, p.location].some(
          (field) => field && field.toLowerCase().includes(term)
        )
      ).slice(0, 12)
    : [];

  const total = (results ? results.total : 0) + matchedPartners.length;

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      <SEO
        title="Search"
        description="Search wedding stories, articles, and trusted wedding partners from I DO Bridal Magazine Malta."
      />

      {/* Search hero */}
      <section className="border-b border-[#E6E2DC]/60 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 md:py-20 text-center">
          <p className="text-xs tracking-[0.3em] uppercase mb-4 text-[#DE92A9]">Site-wide Search</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-[#2C2C2C] mb-8" style={{ fontFamily: '"Playfair Display", serif' }}>
            Find Your Inspiration
          </h1>
          <form onSubmit={handleSubmit} className="relative max-w-2xl mx-auto" data-testid="search-page-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Search weddings, articles, venues, partners..."
              className="w-full border border-[#E6E2DC] bg-[#FAF9F7] px-6 py-4 pr-14 text-sm text-[#2C2C2C] focus:outline-none focus:border-[#DE92A9] transition-colors"
              data-testid="search-page-input"
              autoFocus
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-3 text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#DE92A9' }}
              data-testid="search-page-submit"
              aria-label="Search"
            >
              <Search size={16} />
            </button>
          </form>
          {q.trim().length >= 2 && !loading && (
            <p className="mt-6 text-sm text-[#757B7F]" data-testid="search-results-count">
              {total} result{total === 1 ? '' : 's'} for <span className="text-[#DE92A9] font-medium">"{q}"</span>
            </p>
          )}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        {loading && (
          <div className="text-center py-20" data-testid="search-loading">
            <div className="inline-block w-8 h-8 border-2 border-[#DE92A9] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {!loading && q.trim().length < 2 && (
          <p className="text-center text-[#757B7F] py-16" data-testid="search-empty-prompt">
            Type at least two characters to search wedding stories, articles, and partners.
          </p>
        )}

        {!loading && q.trim().length >= 2 && total === 0 && (
          <div className="text-center py-16" data-testid="search-no-results">
            <p className="text-2xl text-[#2C2C2C] mb-3" style={{ fontFamily: '"Playfair Display", serif' }}>
              Nothing found for "{q}"
            </p>
            <p className="text-sm text-[#757B7F] mb-8">Try a venue, a couple's name, or a planning topic like "flowers" or "music".</p>
            <Link
              to="/wedding-stories"
              className="inline-flex items-center gap-2 text-xs tracking-[0.15em] font-semibold text-white px-8 py-3 transition-opacity hover:opacity-90"
              style={{ backgroundColor: '#DE92A9' }}
              data-testid="search-browse-stories-btn"
            >
              BROWSE WEDDING STORIES <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {/* Wedding stories */}
        {!loading && results && results.weddings.length > 0 && (
          <section className="mb-16" data-testid="search-weddings-section">
            <SectionHeading count={results.weddings.length}>Wedding Stories</SectionHeading>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {results.weddings.map((w) => (
                <Link
                  key={w.slug}
                  to={`/wedding-stories/${w.slug}`}
                  className="group bg-white border border-[#E6E2DC]/60 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  data-testid={`search-wedding-${w.slug}`}
                >
                  <div className="h-56 overflow-hidden">
                    <img
                      src={w.image}
                      alt={`${w.couple} wedding at ${w.venue}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl text-[#2C2C2C] mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
                      {w.couple}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-[#757B7F] mb-3">
                      <span className="inline-flex items-center gap-1"><Calendar size={12} /> {w.date}</span>
                    </div>
                    <p className="text-xs text-[#757B7F] inline-flex items-start gap-1">
                      <MapPin size={12} className="mt-0.5 shrink-0 text-[#DE92A9]" /> {w.venue}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Articles */}
        {!loading && results && results.articles.length > 0 && (
          <section className="mb-16" data-testid="search-articles-section">
            <SectionHeading count={results.articles.length}>Articles</SectionHeading>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {results.articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/tips-inspiration/${a.slug}`}
                  className="group bg-white border border-[#E6E2DC]/60 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  data-testid={`search-article-${a.slug}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={a.image}
                      alt={a.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#DE92A9] font-semibold">{a.category}</span>
                    <h3 className="text-lg text-[#2C2C2C] mt-2 mb-2 leading-snug" style={{ fontFamily: '"Playfair Display", serif' }}>
                      {a.title}
                    </h3>
                    <p className="text-xs text-[#757B7F] line-clamp-2">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Partners */}
        {!loading && matchedPartners.length > 0 && (
          <section className="mb-16" data-testid="search-partners-section">
            <SectionHeading count={matchedPartners.length}>Partners</SectionHeading>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {matchedPartners.map((p) => (
                <Link
                  key={p.id}
                  to={`/partners/${getPartnerSlug(p)}`}
                  className="group bg-white border border-[#E6E2DC]/60 overflow-hidden hover:shadow-lg transition-shadow duration-300"
                  data-testid={`search-partner-${p.id}`}
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={p.image}
                      alt={`${p.name}, ${p.category} in Malta`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#DE92A9] font-semibold">{p.category}</span>
                    <h3 className="text-base text-[#2C2C2C] mt-1 flex items-center gap-1.5" style={{ fontFamily: '"Playfair Display", serif' }}>
                      {p.name}
                    </h3>
                    <p className="text-xs text-[#757B7F] mt-1">{p.location}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
